import Base from '@domain/composition/Base';
import { instanceofType } from './utils';
import Toggles from '@domain/toggles/Toggles';
import { isRelevantArticleType } from '@domain/catalog/ArticleTypeRelevance';

export default class DomainElementsFilter {
    static applyFilter<T extends Base = Base>(
        elements: Array<T>,
        method: 'models' | 'partialModel' | 'applicationLocation' | 'application' | 'location' | 'generic' | 'type' | 'status' | 'control' | 'trackSystem' | 'relevantArticleTypes',
        value?,
        negate: boolean = false
    ) {
        let result: Array<T> = []
        switch (method) {
            case 'models':
                result = this.filterByModels(elements, value)
                break;
            case 'partialModel':
                result = this.filterByPartialModel(elements, value)
                break;
            case 'applicationLocation':
                result = this.filterByApplicationLocation(elements, value)
                break;
            case 'application':
                result = this.filterByApplication(elements, value)
                break;
            case 'location':
                result = this.filterByLocation(elements, value)
                break;
            case 'type':
                result = this.filterByType(elements, value)
                break;
            case 'generic':
                result = this.filterGenericModels(elements)
                break;
            case 'status':
                result = this.filterByStatus(elements, value)
                break;
            case 'control':
                result = this.filterByControl(elements, value)
                break;
            case 'trackSystem':
                result = this.filterByTrackSystem(elements, value)
                break;
            case 'relevantArticleTypes':
                result = this.filterByRelevantArticleTypes(elements)
                break;
            default:
                throw 'Cannot filter by this method'
        }
        if (negate) {
            return elements.filter(elem => !result.includes(elem))
        } else {
            return result
        }
    }

    private static filterByPartialModel<T extends Base = Base>(elements: Array<T>, partialModel: string) {
        if (!partialModel || partialModel.trim() === '') return elements
        const filteredElements = elements.filter(elem => elem.getModel()?.includes(partialModel))
        return filteredElements
    }

    private static filterByModels<T extends Base = Base>(elements: Array<T>, models: Array<string>) {
        const filteredElements = elements.filter(elem => models.includes(elem.getModel()))
        return filteredElements
    }

    private static filterByApplicationLocation<T extends Base = Base>(elements: Array<T>, applicationLocation: number | string) {
        const filteredElements = elements.filter(elem => {
            const lights = elem.getLights()
            if (!lights.length) {
                if (!Toggles.isEnabled('filterAccessoriesByApplicationLocation')) return true
                return elem.getApplicationLocation().some(el => el.id == applicationLocation)
            }
            return lights.every(light => light.element.getApplicationLocation()
                .some(el => el.id == applicationLocation)
            )
        })
        return filteredElements
    }

    private static filterByStatus<T extends Base = Base>(elements: Array<T>, status: 'isComposable' | 'isBundleable' | 'isIndividual') {
        const filteredElements = elements
            .filter((element) => {
                const boolean = Boolean((element as unknown as any).getStatus?.(status))
                return boolean
            })

        return filteredElements
    }

    private static filterByApplication<T extends Base = Base>(elements: Array<T>, application: number | string) {
        const filteredElements = elements.filter(elem => {
            const lights = elem.getLights()
            if (!lights.length) {
                if (!Toggles.isEnabled('filterAccessoriesByApplicationLocation')) return true
                return elem.getApplicationLocation().some(el => el.application?.id == application)
            }
            return lights.every(light => light.element.getApplicationLocation()
                .some(el => el.application?.id == application)
            )
        })
        return filteredElements
    }

    private static filterByLocation<T extends Base = Base>(elements: Array<T>, location: number | string) {
        const filteredElements = elements.filter(elem => {
            const lights = elem.getLights()
            if (!lights.length) {
                if (!Toggles.isEnabled('filterAccessoriesByApplicationLocation')) return true
                return elem.getApplicationLocation().some(el => el.location?.id == location)
            }
            return lights.every(light => light.element.getApplicationLocation()
                .some(el => el.location?.id == location)
            )
        })
        return filteredElements
    }

    private static filterByType<T extends Base = Base>(elements: Array<T>, type: string | Array<string>) {
        if (Array.isArray(type)) {
            const elementsByType = elements.filter(obj => type.some(t => instanceofType(obj, t)));
            return elementsByType
        } else {
            const elementsByType = elements.filter(obj => instanceofType(obj, type));
            return elementsByType
        }
    }

    private static filterGenericModels<T extends Base = Base>(elements: Array<T>) {
        const filteredElements = elements
            .filter(elem => elem.getLights()
                .every(light => {
                    const model = light.element.getModel()
                    return Number(model) > 100
                })
            )
        return filteredElements
    }

    private static filterByControl<T extends Base = Base>(elements: Array<T>, value: string | string[]) {
        if (value == null || value === '') return elements

        const values: string[] = Array.isArray(value) ? value : [value]
        const normalized = values.map(v => String(v).trim().toLowerCase()).filter(v => v.length)
        if (!normalized.length) return elements

        const filteredElements = elements.filter(elem => {
            try {
                const controls = (elem as any).getAttributeVariations?.('control') ?? []
                if (!Array.isArray(controls) || !controls.length) return false
                return controls.some((variation: any) => {
                    const modifier = String(variation?.modifier ?? '').trim().toLowerCase()
                    if (modifier && normalized.includes(modifier)) return true
                    const descriptionObj = variation?.description || {}
                    const descriptions: string[] = Object.values(descriptionObj).map((d: any) => String(d).trim().toLowerCase())
                    return descriptions.some(desc => normalized.includes(desc))
                })
            } catch (e) {
                return false
            }
        })

        return filteredElements
    }

    private static filterByRelevantArticleTypes<T extends Base = Base>(elements: Array<T>): Array<T> {
        return elements.filter(elem => isRelevantArticleType(elem.data?.articleType))
    }

    private static filterByTrackSystem<T extends Base = Base>(elements: Array<T>, isTrackCompatible: boolean = true): Array<T> {
        const filteredElements = elements.filter(elem => {
            const hasTrack = this.hasTrackSystem(elem)
            return isTrackCompatible ? hasTrack : !hasTrack
        })
        return filteredElements
    }

    static hasTrackSystem<T extends Base = Base>(element: T): boolean {
        try {
            // Handle Clusters by accessing their internal lights
            const lights = element.getLights?.();
            if (lights && Array.isArray(lights) && lights.length > 0) {
                // For Clusters/Groups, check the first Light's installations
                const firstLight = lights[0]?.element;
                if (firstLight) {
                    const installations = (firstLight as any).getAttributeVariations?.('installation') ?? []
                    if (Array.isArray(installations) && installations.length) {
                        return installations.some((installation: any) => installation?.trackSystem === true)
                    }
                }
                return false
            }

            const installations = (element as any).getAttributeVariations?.('installation') ?? []
            if (!Array.isArray(installations) || !installations.length) return false
            return installations.some((installation: any) => installation?.trackSystem === true)
        } catch (e) {
            return false
        }
    }

    static groupByTrackSystem<T extends Base = Base>(elements: Array<T>): { track: Array<T>, others: Array<T> } {
        const track: Array<T> = []
        const others: Array<T> = []

        elements.forEach(elem => {
            if (this.hasTrackSystem(elem)) {
                track.push(elem)
            } else {
                others.push(elem)
            }
        })

        return { track, others }
    }
}